import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { VideoPlayer } from '../common/VideoPlayer';
import { ROUTES } from '../../constants/routes';
import {
  buildCourseVideoTarget,
  buildLessonVideoTarget,
  hasPlayableVideoTarget,
} from '../../utils/vdocipherMappers';

function CourseSubscriptionSidebar({
  subscription,
  previewLesson,
  hasFullAccess,
  isFreeCourse,
  isLoggedIn,
  onStartLearning,
  onRequireAuth,
  onRequireSubscription,
}) {
  const [isPlaying, setIsPlaying] = useState(false);

  const previewTarget = useMemo(() => {
    if (previewLesson) {
      return buildLessonVideoTarget(previewLesson);
    }

    if (subscription?.courseId) {
      return buildCourseVideoTarget(subscription.courseId, subscription);
    }

    return null;
  }, [previewLesson, subscription]);

  const hasPreview = hasPlayableVideoTarget(previewTarget);

  const handlePlayPreview = () => {
    if (!hasPreview) return;
    setIsPlaying(true);
  };

  return (
    <aside className="w-full lg:w-[340px]">
      <div className="overflow-hidden rounded-2xl border border-[#E8E0F3] bg-white shadow-[0_8px_32px_rgba(0,0,0,0.12)]">
        <div className="relative">
          {isPlaying && hasPreview ? (
            <VideoPlayer
              target={previewTarget}
              isLoggedIn={isLoggedIn}
              onRequireAuth={onRequireAuth}
              onRequireSubscription={onRequireSubscription}
              autoplay
            />
          ) : (
            <>
              <img
                src={subscription.videoThumbnail}
                alt=""
                className="h-[210px] w-full object-cover"
              />
              {hasPreview ? (
                <button
                  type="button"
                  onClick={handlePlayPreview}
                  className="absolute left-1/2 top-1/2 flex h-[52px] w-[52px] -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-white shadow-lg transition hover:scale-105"
                  aria-label="Play preview"
                >
                  <svg className="ml-0.5 h-6 w-6 text-[#1A87CC]" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M8 5v14l11-7z" />
                  </svg>
                </button>
              ) : null}
            </>
          )}
        </div>

        <div className="m-4 rounded-xl border-2 border-dashed border-[#B8D4E8] p-5">
          {hasFullAccess ? (
            <>
              <h3 className="text-center text-[17px] font-bold text-[#1A87CC]">Learn With Purpose</h3>
              <p className="mt-1 text-center text-sm font-bold text-[#1B1B1B]">
                {isFreeCourse ? 'Start Learning For Free' : 'Your Preparation Starts Now'}
              </p>
            </>
          ) : (
            <>
              <h3 className="text-center text-[17px] font-bold text-[#1A87CC]">Get The Subscription</h3>
              <p className="mt-1 text-center text-sm font-bold text-[#1B1B1B]">Unlock Full Course Access</p>
            </>
          )}

          <ul className="mt-5 space-y-3">
            {subscription.features.map((feature) => (
              <li key={feature} className="flex items-start gap-2.5 text-[13px] font-medium leading-snug text-[#1B1B1B]">
                <span className="mt-0.5 flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full bg-[#9369CC]">
                  <svg className="h-2.5 w-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                </span>
                {feature}
              </li>
            ))}
          </ul>

          {hasFullAccess ? (
            <button
              type="button"
              onClick={onStartLearning}
              className="mt-6 flex w-full items-center justify-center rounded-xl bg-gradient-to-r from-[#9369CC] to-[#6E4F99] py-3.5 text-sm font-bold text-white shadow-md transition hover:opacity-90"
            >
              Start Learning
            </button>
          ) : (
            <Link
              to={ROUTES.PRICING}
              className="mt-6 flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-[#00C3D0] to-[#1A87CC] py-3.5 text-sm font-bold text-white shadow-md transition hover:opacity-90"
            >
              Get Subscription
              <span aria-hidden>👑</span>
            </Link>
          )}
        </div>

        <div className="px-5 pb-6 text-center">
          <p className="text-sm text-[#AAAAAA]">Confused about something?</p>
          <a
            href={`tel:${subscription.phone.replace(/\s/g, '')}`}
            className="mt-3 inline-flex items-center rounded-full bg-[#EDE8F5] px-5 py-2.5 text-sm font-semibold text-[#9369CC] transition hover:bg-[#E0D8F0]"
          >
            call us : {subscription.phone}
          </a>
        </div>
      </div>
    </aside>
  );
}

export default CourseSubscriptionSidebar;
